import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  inr, rupees, unitLabel,
  type OperationKind, type PastingOperation, type PastingUnit, type RateCardDto, type RatesResponse,
} from '../lib/pasting';

/**
 * Super Admin tabs of Production Entry: the pasting contractor's monthly bill
 * and the rate card behind it.
 */

/* ───────────────────────────── contractor bill ───────────────────────────── */

interface BillLine {
  operationId: number | null;
  operation: string;
  /** How these entries differ from the whole job: "without Sorting"… ('' = whole job). */
  variant: string;
  unit: PastingUnit;
  countsAsProduced: boolean;
  rate: number | null;
  qty: number;
  hours: number;
  amount: number | null;
  entries: number;
}

interface BillEntry {
  id: number;
  date: string;
  jobId: string;
  jobName: string;
  operation: string;
  variant: string;
  unit: PastingUnit;
  qty: number;
  hours: number;
  rate: number | null;
  amount: number | null;
}

interface ContractorBillDto {
  key: string;
  contractor: string;
  producedCartons: number;
  lines: BillLine[];
  total: number;
  missingRates: string[];
  entries: BillEntry[];
}

interface BillResponse {
  month: string;
  contractors: ContractorBillDto[];
  grandTotal: number;
}

const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const monthTitle = (ym: string) =>
  new Date(`${ym}-01T00:00:00`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

const dateText = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });

const qtyCell = (unit: PastingUnit, qty: number, hours: number) =>
  unit === 'PER_HOUR' ? `${inr(hours, 2)} h` : inr(qty);

/** "Inner Lock — without Sorting"; the whole job is just the box type. */
const workText = (x: { operation: string; variant?: string }) =>
  (x.variant ? `${x.operation} — ${x.variant}` : x.operation);

export function ContractorBill() {
  const [month, setMonth] = useState(thisMonth);
  const [only, setOnly] = useState('');
  const bill = useQuery({
    enabled: /^\d{4}-\d{2}$/.test(month),
    queryKey: ['pasting-bill', month],
    queryFn: () => api<BillResponse>(`/api/pasting/bill?month=${month}`),
  });

  const shown = (bill.data?.contractors ?? []).filter((k) => !only || k.key === only);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="label">Month</label>
          <input type="month" className="input" value={month} onChange={(e) => { setMonth(e.target.value); setOnly(''); }} />
        </div>
        <div>
          <label className="label">Contractor</label>
          <select className="input min-w-[200px]" value={only} onChange={(e) => setOnly(e.target.value)}>
            <option value="">All contractors</option>
            {(bill.data?.contractors ?? []).map((k) => <option key={k.key} value={k.key}>{k.contractor}</option>)}
          </select>
        </div>
        {bill.data && (
          <div className="ml-auto text-right">
            <div className="text-[10px] uppercase tracking-wide text-slate-500">Total for {monthTitle(month)}</div>
            <div className="text-lg font-bold text-brand">{rupees(shown.reduce((s, k) => s + k.total, 0))}</div>
          </div>
        )}
      </div>

      {bill.isLoading && <p className="text-slate-500 text-sm">Loading…</p>}
      {bill.isError && <p className="text-red-600 text-sm">Could not load the bill: {(bill.error as Error).message}</p>}
      {bill.data && shown.length === 0 && (
        <p className="text-slate-500 text-sm">No production entries in {monthTitle(month)}.</p>
      )}

      {shown.map((k) => <ContractorCard key={k.key} bill={k} month={month} />)}
    </div>
  );
}

function ContractorCard({ bill, month }: { bill: ContractorBillDto; month: string }) {
  const [showEntries, setShowEntries] = useState(false);
  return (
    <div className="card">
      <div className="card-header flex flex-wrap items-center justify-between gap-2">
        <div>
          <span className="font-semibold">{bill.contractor}</span>
          <span className="ml-2 text-xs font-normal text-slate-500">{monthTitle(month)}</span>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-ghost text-xs" onClick={() => printBill(bill, month)}>Print bill</button>
        </div>
      </div>
      <div className="card-body space-y-3">
        <div className="text-xs text-slate-600">
          Produced this month: <b>{inr(bill.producedCartons)}</b> cartons
        </div>
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase text-slate-500 border-b border-slate-200">
            <tr>
              <th className="py-1.5">Box / Work</th>
              <th className="py-1.5 text-right">Qty</th>
              <th className="py-1.5 text-right">Rate</th>
              <th className="py-1.5 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {bill.lines.map((l, i) => (
              <tr key={i} className="border-b border-slate-100">
                <td className="py-1.5">
                  {l.operation}
                  {l.variant && <span className="ml-1.5 text-xs text-slate-500">— {l.variant}</span>}
                  {!l.countsAsProduced && <span className="ml-1.5 text-[10px] text-amber-700">billed only</span>}
                  <span className="ml-1.5 text-[10px] text-slate-400">{l.entries} entr{l.entries === 1 ? 'y' : 'ies'}</span>
                </td>
                <td className="py-1.5 text-right">{qtyCell(l.unit, l.qty, l.hours)}</td>
                <td className="py-1.5 text-right text-slate-600">
                  {l.rate == null ? <span className="text-red-600">no rate</span> : `${rupees(l.rate)} ${unitLabel(l.unit)}`}
                </td>
                <td className="py-1.5 text-right font-medium">{rupees(l.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3} className="py-2 text-right font-semibold">Total</td>
              <td className="py-2 text-right font-bold text-brand">{rupees(bill.total)}</td>
            </tr>
          </tfoot>
        </table>
        {bill.missingRates.length > 0 && (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            No rate set for: {bill.missingRates.join(', ')} — these are left out of the total. Set them on the Rates tab.
          </div>
        )}
        <button className="text-xs text-brand hover:underline" onClick={() => setShowEntries((v) => !v)}>
          {showEntries ? 'Hide' : 'Show'} the {bill.entries.length} entr{bill.entries.length === 1 ? 'y' : 'ies'}
        </button>
        {showEntries && (
          <div className="overflow-x-auto border border-slate-200 rounded">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-left text-[10px] uppercase text-slate-500">
                <tr>
                  <th className="px-2 py-1.5">Date</th>
                  <th className="px-2 py-1.5">Job</th>
                  <th className="px-2 py-1.5">Product</th>
                  <th className="px-2 py-1.5">Box / Work</th>
                  <th className="px-2 py-1.5 text-right">Qty</th>
                  <th className="px-2 py-1.5 text-right">Rate</th>
                  <th className="px-2 py-1.5 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {bill.entries.map((e) => (
                  <tr key={e.id} className="border-t border-slate-100">
                    <td className="px-2 py-1 whitespace-nowrap">{dateText(e.date)}</td>
                    <td className="px-2 py-1 font-mono">{e.jobId || '—'}</td>
                    <td className="px-2 py-1 max-w-[240px] truncate" title={e.jobName}>{e.jobName}</td>
                    <td className="px-2 py-1">{workText(e)}</td>
                    <td className="px-2 py-1 text-right">{qtyCell(e.unit, e.qty, e.hours)}</td>
                    <td className="px-2 py-1 text-right">{e.rate == null ? '—' : inr(e.rate, 2)}</td>
                    <td className="px-2 py-1 text-right">{rupees(e.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** A clean printable bill in a new window — lines, total, then every entry. */
function printBill(bill: ContractorBillDto, month: string) {
  const w = window.open('', '_blank', 'width=900,height=1100');
  if (!w) { alert('The print window was blocked — allow pop-ups for this site.'); return; }
  const td = 'border:1px solid #000;padding:3px 6px;';
  const lines = bill.lines.map((l) => `<tr>
      <td style="${td}">${esc(workText(l))}</td>
      <td style="${td}text-align:right">${qtyCell(l.unit, l.qty, l.hours)}</td>
      <td style="${td}text-align:right">${l.rate == null ? 'no rate' : `${inr(l.rate, 2)} ${unitLabel(l.unit)}`}</td>
      <td style="${td}text-align:right">${l.amount == null ? '—' : inr(l.amount, 2)}</td></tr>`).join('');
  const entries = bill.entries.map((e) => `<tr>
      <td style="${td}">${dateText(e.date)}</td><td style="${td}">${esc(e.jobId || '—')}</td>
      <td style="${td}">${esc(e.jobName)}</td><td style="${td}">${esc(workText(e))}</td>
      <td style="${td}text-align:right">${qtyCell(e.unit, e.qty, e.hours)}</td>
      <td style="${td}text-align:right">${e.amount == null ? '—' : inr(e.amount, 2)}</td></tr>`).join('');
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Pasting bill ${esc(bill.contractor)} ${month}</title>
    <style>@page{size:A4;margin:10mm} body{font-family:Arial,Helvetica,sans-serif;font-size:11px;color:#000;margin:0}
    table{border-collapse:collapse;width:100%} th{border:1px solid #000;padding:3px 6px;background:#f2f2f2;text-align:left}</style></head><body>
    <div style="text-align:center;font-weight:bold;font-size:15px">VASUDEV PRINT PACK PRIVATE LIMITED</div>
    <div style="text-align:center;font-weight:bold;font-size:13px;margin:4px 0 10px">PASTING CONTRACTOR BILL — ${esc(monthTitle(month))}</div>
    <div style="margin-bottom:6px"><b>Contractor:</b> ${esc(bill.contractor)}</div>
    <div style="margin-bottom:10px"><b>Produced cartons:</b> ${inr(bill.producedCartons)}</div>
    <table><thead><tr><th>Box / Work</th><th style="text-align:right">Qty</th><th style="text-align:right">Rate</th><th style="text-align:right">Amount (₹)</th></tr></thead>
    <tbody>${lines}<tr><td colspan="3" style="${td}text-align:right;font-weight:bold">Total</td><td style="${td}text-align:right;font-weight:bold">${inr(bill.total, 2)}</td></tr></tbody></table>
    ${bill.missingRates.length ? `<p style="margin-top:6px">No rate set (not in total): ${esc(bill.missingRates.join(', '))}</p>` : ''}
    <div style="margin-top:14px;font-weight:bold">Entries</div>
    <table style="margin-top:4px"><thead><tr><th>Date</th><th>Job</th><th>Product</th><th>Box / Work</th><th style="text-align:right">Qty</th><th style="text-align:right">Amount (₹)</th></tr></thead>
    <tbody>${entries}</tbody></table>
    <div style="display:flex;justify-content:space-between;margin-top:40px"><span>Contractor's signature</span><span>Authorised Signatory</span></div>
    </body></html>`);
  w.document.close();
  w.addEventListener('afterprint', () => w.close());
  setTimeout(() => { w.focus(); w.print(); }, 300);
}

/* ────────────────────────────────── rates ────────────────────────────────── */

/** The rate table's sections, in order; switched-off operations go last. */
const SECTIONS: { kind: OperationKind; title: string; hint: string }[] = [
  { kind: 'BOX', title: 'Box types', hint: 'price per 1000 for the whole job — its steps and the pasting are equal shares of it' },
  { kind: 'STEP', title: 'Steps', hint: 'tick boxes on Production Entry; each is an equal share of the box price, as is the pasting — no price of their own' },
  { kind: 'EXTRA', title: 'Other work', hint: 'entered on its own' },
];

const producedText = (o: PastingOperation) =>
  o.kind === 'BOX' ? (o.countsAsProduced ? 'counts when pasted' : 'billed only')
    : o.kind === 'STEP' ? 'part of the box' : 'billed only';

const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fmtDate = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

/** The form's rate boxes from a card: operation → its rate as typed ('' = none). */
function draftFrom(card: RateCardDto | undefined, ops: PastingOperation[]): Record<number, string> {
  const out: Record<number, string> = {};
  for (const o of ops) {
    const r = card?.rates.find((x) => x.operationId === o.id);
    out[o.id] = r?.rate == null ? '' : String(r.rate);
  }
  return out;
}

export function PastingRatesEditor() {
  const qc = useQueryClient();
  const data = useQuery({
    queryKey: ['pasting-rates'],
    queryFn: () => api<RatesResponse>('/api/pasting/rates'),
  });
  const ops = data.data?.operations ?? [];
  const cards = data.data?.cards ?? [];
  const current = cards.find((c) => c.id === data.data?.currentCardId);

  const [effectiveFrom, setEffectiveFrom] = useState(todayIso);
  const [notes, setNotes] = useState('');
  const [rates, setRates] = useState<Record<number, string>>({});
  const [loadedFrom, setLoadedFrom] = useState<number | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Start the form from the card in force today.
  useEffect(() => {
    if (!data.data || loadedFrom != null) return;
    setRates(draftFrom(current, ops));
    setLoadedFrom(current?.id ?? 0);
  }, [data.data, current, ops, loadedFrom]);

  // Operations added after the form was filled get an empty row.
  useEffect(() => {
    setRates((r) => {
      const missing = ops.filter((o) => !(o.id in r));
      if (!missing.length) return r;
      const next = { ...r };
      for (const o of missing) next[o.id] = '';
      return next;
    });
  }, [ops]);

  function loadCard(card: RateCardDto) {
    setRates(draftFrom(card, ops));
    setNotes(card.notes);
    setEffectiveFrom(card.effectiveFrom);
    setLoadedFrom(card.id);
    setMsg(null);
  }

  const existingOnDate = cards.find((c) => c.effectiveFrom === effectiveFrom);

  const sections = [
    ...SECTIONS.map((sec) => ({ ...sec, ops: ops.filter((o) => o.isActive && o.kind === sec.kind) })),
    { kind: 'EXTRA' as OperationKind, title: 'Switched off', hint: 'not offered on Production Entry; old entries keep these rates', ops: ops.filter((o) => !o.isActive) },
  ].filter((sec) => sec.ops.length > 0);

  // A worked example from the rates in the form: a box type's parts are its
  // steps plus the pasting, equal shares of its price.
  const example = (() => {
    const boxes = ops.filter((o) => o.isActive && o.kind === 'BOX');
    const steps = ops.filter((o) => o.isActive && o.kind === 'STEP');
    const box = boxes[1] ?? boxes[0];
    const step = steps[1] ?? steps[0];
    const b = Number(rates[box?.id ?? -1]);
    if (!box || !step || !(rates[box.id] ?? '').trim() || !(b >= 0)) return null;
    const parts = steps.length + 1;
    const share = Math.round((b / parts) * 100) / 100;
    const less = Math.round(((b * (parts - 1)) / parts) * 100) / 100;
    return `${box.name} at ${inr(b, 2)} has ${parts} parts (${[...steps.map((x) => x.name), 'Pasting'].join(', ')}), `
      + `${inr(share, 2)} each — without ${step.name} it is paid ${inr(less, 2)} per 1000.`;
  })();

  const save = useMutation({
    mutationFn: (body: object) => api<{ ok: boolean; id: number }>('/api/pasting/rates', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pasting-rates'] });
      qc.invalidateQueries({ queryKey: ['pasting-bill'] });
      qc.invalidateQueries({ queryKey: ['production'] });
      setMsg({ ok: true, text: `Saved. Entries from ${fmtDate(effectiveFrom)} use these rates; earlier entries keep the rates they had.` });
    },
    onError: (e) => setMsg({ ok: false, text: (e as Error).message }),
  });

  function submit() {
    setMsg(null);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom)) { setMsg({ ok: false, text: 'Pick the date these rates start from.' }); return; }
    const parse = (v: string) => (v.trim() === '' ? null : Number(v));
    const list = ops.map((o) => ({ operationId: o.id, rate: parse(rates[o.id] ?? '') }));
    if (list.some((r) => r.rate != null && !(r.rate >= 0))) {
      setMsg({ ok: false, text: 'Rates must be numbers (leave blank for no rate).' });
      return;
    }
    if (existingOnDate && !window.confirm(`Rates starting ${fmtDate(effectiveFrom)} already exist — replace them?`)) return;
    save.mutate({ effectiveFrom, notes: notes.trim() || null, rates: list });
  }

  const toggle = useMutation({
    mutationFn: (o: PastingOperation) => api(`/api/pasting/operations/${o.id}`, { method: 'PUT', body: JSON.stringify({ isActive: !o.isActive }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pasting-rates'] });
      qc.invalidateQueries({ queryKey: ['pasting-operations'] });
    },
  });

  // Billed-only work whose cartons go back to Quality — Resorting.
  const toggleQc = useMutation({
    mutationFn: (o: PastingOperation) => api(`/api/pasting/operations/${o.id}`, { method: 'PUT', body: JSON.stringify({ needsQc: !o.needsQc }) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['pasting-rates'] });
      qc.invalidateQueries({ queryKey: ['pasting-operations'] });
    },
    onError: (e) => setMsg({
      ok: false,
      text: (e as Error).message === 'qc_resorting_migration_pending'
        ? 'The server database needs its update (Resorting to Quality) first.'
        : (e as Error).message,
    }),
  });

  // Only what no entry has used can go; the rest can be switched off.
  const remove = useMutation({
    mutationFn: (o: PastingOperation) =>
      api<{ ok: boolean; name: string }>(`/api/pasting/operations/${o.id}`, { method: 'DELETE' }),
    onSuccess: (r) => {
      setMsg({ ok: true, text: `Deleted "${r.name}".` });
      qc.invalidateQueries({ queryKey: ['pasting-rates'] });
      qc.invalidateQueries({ queryKey: ['pasting-operations'] });
    },
    onError: (e, o) => {
      const used = /^operation_in_use:(\d+)$/.exec((e as Error).message);
      setMsg({
        ok: false,
        text: used
          ? `"${o.name}" is on ${used[1]} production entr${used[1] === '1' ? 'y' : 'ies'}, so it can't be deleted — switch it off instead (In use → No).`
          : (e as Error).message,
      });
    },
  });

  if (data.isLoading) return <p className="text-slate-500 text-sm">Loading…</p>;
  if (data.isError) return <p className="text-red-600 text-sm">Could not load rates: {(data.error as Error).message}</p>;

  return (
    <div className="space-y-4">
      <div className="card">
        <div className="card-header">Pasting contractor rates</div>
        <div className="card-body space-y-3">
          <p className="text-xs text-slate-500">
            {current
              ? <>In force today: rates from <b>{fmtDate(current.effectiveFrom)}</b>.</>
              : 'No rates saved yet.'}
            {' '}New rates apply from their start date — older entries keep the rates they had.
          </p>

          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="label">Rates start from *</label>
              <input type="date" className="input" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
              {existingOnDate && <p className="text-[11px] text-amber-700 mt-0.5">Saving replaces the rates that start on this date.</p>}
            </div>
            <div>
              <label className="label">Note</label>
              <input className="input" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. renegotiated Oct 2026" />
            </div>
          </div>

          <div className="overflow-x-auto border border-slate-200 rounded">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-[11px] uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2">Box type / step / work</th>
                  <th className="px-3 py-2">Unit</th>
                  <th className="px-3 py-2">Produced qty</th>
                  <th className="px-3 py-2 text-right">Rate ₹</th>
                  <th className="px-3 py-2 text-right">In use</th>
                  <th className="px-3 py-2"><span className="sr-only">Delete</span></th>
                </tr>
              </thead>
              {sections.map((sec) => (
              <tbody key={sec.title}>
                <tr className="border-t border-slate-200 bg-slate-50">
                  <td colSpan={6} className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
                    {sec.title} <span className="normal-case font-normal tracking-normal text-slate-500">— {sec.hint}</span>
                  </td>
                </tr>
                {sec.ops.map((o) => (
                  <tr key={o.id} className={`border-t border-slate-100 ${o.isActive ? '' : 'opacity-50'}`}>
                    <td className="px-3 py-1.5">{o.name}</td>
                    <td className="px-3 py-1.5 text-xs text-slate-600">{unitLabel(o.unit)}</td>
                    <td className="px-3 py-1.5 text-xs">
                      {producedText(o) === 'billed only' ? <span className="text-amber-700">billed only</span> : producedText(o)}
                      {o.kind === 'EXTRA' && o.unit === 'PER_1000' && (
                        <button
                          className="ml-1.5 text-brand hover:underline"
                          disabled={toggleQc.isPending}
                          onClick={() => toggleQc.mutate(o)}
                          title="Yes: its entries wait for Quality to approve them again (cartons rejected and sorted again). Approved cartons can then be dispatched."
                        >· Quality check: {o.needsQc ? 'Yes' : 'No'}</button>
                      )}
                    </td>
                    {o.kind === 'STEP' ? (
                      // A step is an equal share of the box price: nothing to enter.
                      <td className="px-3 py-1.5 text-right text-xs text-slate-500">equal share of the box price</td>
                    ) : (
                      <td className="px-3 py-1.5 text-right">
                        <input type="number" min={0} step="any" className="input w-28 text-right ml-auto"
                          value={rates[o.id] ?? ''}
                          onChange={(e) => setRates((r) => ({ ...r, [o.id]: e.target.value }))} />
                      </td>
                    )}
                    <td className="px-3 py-1.5 text-right">
                      <button className="text-xs text-brand hover:underline" disabled={toggle.isPending} onClick={() => toggle.mutate(o)}
                        title={o.isActive ? 'Hide from the Production Entry list' : 'Show in the Production Entry list again'}>
                        {o.isActive ? 'Yes' : 'No'}
                      </button>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <button className="text-xs text-red-600 hover:underline" disabled={remove.isPending}
                        onClick={() => {
                          if (window.confirm(`Delete "${o.name}"? It goes from this list and from Production Entry, with its rates.`)) remove.mutate(o);
                        }}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              ))}
            </table>
          </div>
          {example && <p className="text-[11px] text-slate-500">For example — {example}</p>}

          {msg && <div className={`text-sm ${msg.ok ? 'text-emerald-700' : 'text-red-700'}`}>{msg.text}</div>}
          <div className="flex justify-end">
            <button className="btn-primary" onClick={submit} disabled={save.isPending}>
              {save.isPending ? 'Saving…' : existingOnDate ? 'Replace these rates' : 'Save new rates'}
            </button>
          </div>
        </div>
      </div>

      <AddOperation />

      <div className="card">
        <div className="card-header">Rate history</div>
        <div className="card-body">
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase text-slate-500 border-b border-slate-200">
              <tr>
                <th className="py-1.5">Starts from</th>
                <th className="py-1.5">Note</th>
                <th className="py-1.5">Saved by</th>
                <th className="py-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {[...cards].reverse().map((c) => (
                <tr key={c.id} className="border-b border-slate-100">
                  <td className="py-1.5">
                    {fmtDate(c.effectiveFrom)}
                    {c.id === data.data?.currentCardId && <span className="ml-1.5 text-[10px] font-semibold text-emerald-700">in force</span>}
                  </td>
                  <td className="py-1.5 text-xs text-slate-600">{c.notes || '—'}</td>
                  <td className="py-1.5 text-xs text-slate-600">{c.createdByName ?? '—'}</td>
                  <td className="py-1.5 text-right">
                    <button className="text-xs text-brand hover:underline" onClick={() => loadCard(c)}>Load into form</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function AddOperation() {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [kind, setKind] = useState<OperationKind>('BOX');
  const [unit, setUnit] = useState<PastingUnit>('PER_1000');
  const [err, setErr] = useState<string | null>(null);
  const add = useMutation({
    // A box type adds to produced qty when pasted; steps and other work never do.
    mutationFn: () => api('/api/pasting/operations', {
      method: 'POST',
      body: JSON.stringify({ name: name.trim(), kind, unit: kind === 'EXTRA' ? unit : 'PER_1000', countsAsProduced: kind === 'BOX' }),
    }),
    onSuccess: () => {
      setName(''); setKind('BOX'); setUnit('PER_1000'); setErr(null);
      qc.invalidateQueries({ queryKey: ['pasting-rates'] });
      qc.invalidateQueries({ queryKey: ['pasting-operations'] });
    },
    onError: (e) => setErr((e as Error).message === 'operation_exists' ? 'An operation with that name already exists.' : (e as Error).message),
  });
  const canAdd = useMemo(() => name.trim().length > 0, [name]);
  return (
    <div className="card">
      <div className="card-header">Add a box type, step or other work</div>
      <div className="card-body">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <label className="label">Name</label>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Crash Lock" />
          </div>
          <div>
            <label className="label">Kind</label>
            <select className="input" value={kind} onChange={(e) => setKind(e.target.value as OperationKind)}>
              <option value="BOX">Box type — whole job, counts when pasted</option>
              <option value="STEP">Step — a tick box, an equal share of the box price</option>
              <option value="EXTRA">Other work — billed only</option>
            </select>
          </div>
          {kind === 'EXTRA' && (
            <div>
              <label className="label">Paid</label>
              <select className="input" value={unit} onChange={(e) => setUnit(e.target.value as PastingUnit)}>
                <option value="PER_1000">per 1000 cartons</option>
                <option value="PER_HOUR">per hour</option>
              </select>
            </div>
          )}
          <button className="btn-primary" disabled={!canAdd || add.isPending} onClick={() => add.mutate()}>Add</button>
        </div>
        {err && <p className="text-sm text-red-700 mt-2">{err}</p>}
        <p className="text-[11px] text-slate-500 mt-2">
          {kind === 'STEP'
            ? 'A step needs no rate: it becomes one more tick box, and every box price is then shared by one more part.'
            : 'Then enter its rate in the table and save.'}
        </p>
      </div>
    </div>
  );
}
